import React, { useCallback, useMemo, useState, forwardRef, useImperativeHandle } from "react";
import { Formik } from "formik";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import MemberSwitchList from "components/UnprocessedFaxList/MemberSwitchList";
import PrescriberListV2 from "components/UnprocessedFaxList/PrescriberListV2";
import UnprocessedFaxCustomList from "components/UnprocessedFaxList/UnprocessedFaxCustomList";
import DiscardFax from "components/UnprocessedFaxDetails/DiscardFax";
import CompleteLinkingButton from "components/UnprocessedFaxDetails/CompleteLinkingButton";
import UnprocessedFaxLinkPageFields, {
  buildUnprocessedFaxLinkValidationSchema,
  isLinkPageFieldMissing,
} from "components/UnprocessedFaxDetails/UnprocessedFaxLinkPageFields";
import { formatValueWithInputMask } from "commons/utils/inputMask.utils";
import { UNPROCESSED_FAX_PROGRAM_TYPE } from "lookup/UnprocessedFaxListConstantsV2";

const UnprocessedFaxLinkSection = forwardRef(
  (
    {
      faxInfoId,
      faxTypeConfig,
      linkFieldsConfig = [],
      noOfPages,
      formikRef,
      onSubmit,
      linkAndArchiveLoading,
      hasLinkedPages,
      discardFax,
      completePageLinking,
      resetPrescriberSelection,
      resetSwitchSelection,
      resetCustomListSelection,
      onCustomListReset,
      note,
      noteObject,
      setNote,
      onSaveOrUpdateNote,
      saveNoteLoading,
      updateNoteLoading,
      deleteNoteLoading,
      onDeleteNote,
    },
    ref
  ) => {
    const [disableLinking, setDisableLinking] = useState(true);

    const validationSchema = useMemo(
      () => buildUnprocessedFaxLinkValidationSchema(linkFieldsConfig, noOfPages),
      [linkFieldsConfig, noOfPages]
    );

    const onChangeLinkPrescribers = useCallback((prescriberNpis, values, setValues) => {
      const linkPages = [];
      prescriberNpis.forEach((prescriberNpi) => {
        const prescriberInfo = values.linkPages.find((x) => x.identifier === prescriberNpi);
        if (prescriberInfo) {
          linkPages.push(prescriberInfo);
        } else {
          linkPages.push({
            isUpsideDown: false,
            overrideAssociatedSwitch: false,
            identifier: prescriberNpi,
            referenceField: null,
            additionalField: null,
          });
        }
      });
      setValues({ ...values, linkPages });
      setDisableLinking(prescriberNpis.length === 0);
    }, []);

    const customListConfig = faxTypeConfig.showCustomList;

    const buildLinkPageFromCustomListRecord = useCallback(
      (record, selection) => {
        const linkPageField = selection?.linkPageField || "identifier";
        const rowIdKey = selection?.rowIdKey || "id";
        const rowKeyValue = record[rowIdKey] ?? record.id;
        const populateFields = selection?.populateFields;

        const linkPage = {
          isUpsideDown: false,
          overrideAssociatedSwitch: false,
          identifier: null,
          referenceField: null,
          additionalField: null,
        };

        if (Array.isArray(populateFields) && populateFields.length > 0) {
          populateFields.forEach(({ from, to, formatMask }) => {
            if (from && to && record[from] != null && record[from] !== "") {
              const rawValue = record[from];
              linkPage[to] = formatMask
                ? formatValueWithInputMask(rawValue, formatMask)
                : rawValue;
            }
          });
        } else if (linkPageField) {
          const formatMask = selection?.valueFormatMask;
          linkPage[linkPageField] = formatMask
            ? formatValueWithInputMask(rowKeyValue, formatMask)
            : rowKeyValue;
        }

        return linkPage;
      },
      []
    );

    const onChangeLinkCustomList = useCallback(
      (selectedIds, values, setValues, selectedRecords = []) => {
        if (!customListConfig) return;

        const selection = customListConfig.selection || {};
        const linkPageField = selection.linkPageField || "identifier";
        const linkPages = [];

        selectedRecords.forEach((record) => {
          const builtLinkPage = buildLinkPageFromCustomListRecord(record, selection);
          const builtFieldValue = builtLinkPage[linkPageField];
          const existing = values.linkPages.find(
            (page) => page[linkPageField] === builtFieldValue
          );

          if (existing) {
            linkPages.push({
              ...existing,
              ...builtLinkPage,
            });
          } else {
            linkPages.push(builtLinkPage);
          }
        });

        setValues({ ...values, linkPages });
        setDisableLinking(selectedIds.length === 0);
      },
      [customListConfig, buildLinkPageFromCustomListRecord]
    );

    const onChangeLinkSwitches = useCallback((globalSwitchIds, values, setValues) => {
      const linkPages = [];
      globalSwitchIds.forEach((globalSwitchId) => {
        const switchInfo = values.linkPages.find((x) => x.identifier === globalSwitchId);
        if (switchInfo) {
          linkPages.push(switchInfo);
        } else {
          linkPages.push({
            isUpsideDown: false,
            overrideAssociatedSwitch: false,
            identifier: globalSwitchId,
            referenceField: null,
            additionalField: null,
          });
        }
      });
      setValues({ ...values, linkPages });
      setDisableLinking(globalSwitchIds.length === 0);
    }, []);

    useImperativeHandle(ref, () => ({
      resetLinkForm() {
        if (!formikRef.current) return;
        const resetValues = {
          faxId: faxInfoId,
          linkPages: [],
        };
        formikRef.current.resetForm({ values: resetValues });
        onChangeLinkPrescribers([], resetValues, formikRef.current.setValues);
        onChangeLinkSwitches([], resetValues, formikRef.current.setValues);
        onChangeLinkCustomList([], resetValues, formikRef.current.setValues);
        onCustomListReset?.();
      },
    }));

    return (
      <>
        <hr />
        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={{
            faxId: faxInfoId,
            linkPages: [],
          }}
          onSubmit={(values, { setSubmitting }) => onSubmit(values, setSubmitting)}
          validationSchema={validationSchema}
        >
          {({ values, handleSubmit, isSubmitting, setValues }) => (
            <Form onSubmit={handleSubmit}>
              <UnprocessedFaxLinkPageFields
                linkFieldsConfig={linkFieldsConfig}
                noOfPages={noOfPages}
              />
              <Row>
                <Col md={6} className="d-grid gap-2">
                  <Button
                    id="unprocessed-fax-btn-link"
                    block
                    variant="success"
                    type="submit"
                    disabled={
                      isSubmitting ||
                      disableLinking ||
                      (faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH &&
                        (linkAndArchiveLoading ||
                          values.linkPages.some((page) =>
                            isLinkPageFieldMissing(page, linkFieldsConfig)
                          )))
                    }
                  >
                    {faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH &&
                    linkAndArchiveLoading
                      ? "Linking and Archiving..."
                      : faxTypeConfig.linkButtonLabel}
                  </Button>
                </Col>
                {faxTypeConfig.showDiscard && (
                  <Col md={6}>
                    {hasLinkedPages ? (
                      <CompleteLinkingButton
                        disallowEditing={isSubmitting || linkAndArchiveLoading}
                        completeLinking={completePageLinking}
                        faxId={faxInfoId}
                      />
                    ) : (
                      <DiscardFax
                        disallowEditing={isSubmitting}
                        discardFax={discardFax}
                        faxId={faxInfoId}
                      />
                    )}
                  </Col>
                )}
              </Row>
              {faxTypeConfig.showMemberSwitchList && (
                <MemberSwitchList
                  setSelectedSwitches={onChangeLinkSwitches}
                  values={values}
                  setValues={setValues}
                  resetTableSelection={resetSwitchSelection}
                  dataTestId="member-switch-list"
                />
              )}
              {faxTypeConfig.showPrescriberList && (
                <PrescriberListV2
                  setSelectedPrescribers={onChangeLinkPrescribers}
                  values={values}
                  setValues={setValues}
                  resetTableSelection={resetPrescriberSelection}
                  dataTestId="prescriber-list"
                />
              )}
              {customListConfig && (
                <UnprocessedFaxCustomList
                  config={customListConfig}
                  faxInfoId={faxInfoId}
                  setSelectedRows={onChangeLinkCustomList}
                  values={values}
                  setValues={setValues}
                  resetTableSelection={resetCustomListSelection}
                  dataTestId={customListConfig.id || "unprocessed-fax-custom-list"}
                />
              )}
              {faxTypeConfig.showNotes && (
                <>
                  <Form>
                    <Form.Group>
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="notes"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </Form.Group>
                  </Form>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "10px",
                    }}
                  >
                    {noteObject?.uflNoteUuid?.length > 0 ? (
                      <>
                        <Button
                          variant="primary"
                          onClick={onSaveOrUpdateNote}
                          style={{ marginRight: "10px" }}
                          disabled={note.length === 0 || updateNoteLoading}
                        >
                          {updateNoteLoading ? "Updating..." : "Update Note"}
                        </Button>
                        <Button
                          variant="danger"
                          onClick={onDeleteNote}
                          disabled={deleteNoteLoading}
                        >
                          {deleteNoteLoading ? "Deleting..." : "Delete Note"}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="primary"
                        onClick={onSaveOrUpdateNote}
                        disabled={note.length === 0 || saveNoteLoading}
                      >
                        {saveNoteLoading ? "Saving..." : "Save Note"}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </Form>
          )}
        </Formik>
      </>
    );
  }
);

UnprocessedFaxLinkSection.displayName = "UnprocessedFaxLinkSection";

export default UnprocessedFaxLinkSection;
